import Section from "@/components/settings/Section";
import SlotDurationSelector from "@/components/shared/SlotDurationSelector";
import { TimeSlot, WeekCalendar } from "@/components/shared/WeekCalendar";
import usePagination from "@/utils/usePagination";
import { addDays, startOfWeek } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { User } from "snudbud-backbone-sdk";

interface AvailabilityScheduleSettingsProps {
  userId: string;
  api: any;
  updateUser: (load?: boolean) => Promise<void>;
}

function AvailabilityScheduleSettings({ userId, api }: AvailabilityScheduleSettingsProps) {
  const [error, setError] = useState<string | null>(null);
  const [slotDuration, setSlotDuration] = useState<TimeSlot["duration"]>(15);
  const [weekStart, setWeekStart] = useState<Date>(() => startOfWeek(new Date(), { weekStartsOn: 1 }));

  const pagination = usePagination<User.Schedule.QueryOwnReturn["slots"][0]>({
    itemsPerPage: 100,
    initial: false,
    fn: async ({ limit, offset }) => {
      const res = await api.user.schedule.queryOwn({ start: weekStart, limit, offset });
      return { count: res.total ?? res.slots.length, items: res.slots };
    },
  });

  const timeSlots = useMemo<TimeSlot[]>(
    () =>
      pagination.data.map((s) => ({
        id: s.scheduleSlotId,
        startDate: new Date(s.startsAt),
        duration: s.duration as TimeSlot["duration"],
        persisted: true,
        bookedByUser: s.bookedByUser ?? null,
      })),
    [pagination.data]
  );

  const weekSlots = useMemo(() => {
    const end = addDays(weekStart, 7);
    return timeSlots.filter((s) => s.startDate >= weekStart && s.startDate < end);
  }, [timeSlots, weekStart]);

  const showError = (msg: string) => {
    setError(msg);
    setTimeout(() => setError(null), 4_000);
  };

  const addSlots = async (newSlots: TimeSlot[]) => {
    try {
      await Promise.all(newSlots.map((s) => api.user.schedule.create({ startsAt: s.startDate, duration: s.duration })));
      await pagination.refresh();
    } catch (err: any) {
      showError(err?.message ?? "Failed to add slot");
      await pagination.refresh();
    }
  };

  const deleteSlot = async (slotId: string) => {
    const prev = pagination.data;
    pagination.setData(prev.filter((s) => s.scheduleSlotId !== slotId));
    try {
      await api.user.schedule.delete({ scheduleSlotId: slotId });
    } catch (err: any) {
      showError(err?.message ?? "Failed to delete slot");
      pagination.setData(prev);
    }
  };

  useEffect(() => {
    if (!userId) return;

    // initial load (refresh resets internal paging state)
    pagination.refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    /**
     * Keep loading pages until one of the following is true:
     *  1. There are no more pages (data.length >= count)
     *  2. The last fetched slot starts AFTER the end of the visible week
     */
    const maybeLoadMore = async () => {
      if (pagination.isLoading || pagination.isRefreshing) return;
      if (pagination.data.length >= pagination.count) return;

      const weekEnd = addDays(weekStart, 7);
      const last = pagination.data[pagination.data.length - 1];

      // no data yet – request first page
      if (!last) return await pagination.load();

      // While the last slot is BEFORE weekEnd, keep loading
      const lastDate = new Date(last.startsAt);
      if (lastDate >= weekEnd) return;

      await pagination.load();
    };

    maybeLoadMore();
  }, [pagination.data]);

  useEffect(() => {
    pagination.refresh();
  }, [weekStart]);

  return (
    <Section title="Language Practice Availability" id="availability">
      {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">{error}</div>}

      <div className="mb-6">
        <SlotDurationSelector value={slotDuration} onChange={setSlotDuration} />
      </div>

      {/* Mobile view explanation */}
      <div className="md:hidden mb-4 text-sm text-gray-600">
        <p>Set your availability for language practice. Other users will be able to book the slots.</p>
      </div>

      <WeekCalendar
        slots={weekSlots}
        slotDuration={slotDuration}
        onAdd={addSlots}
        onDelete={deleteSlot}
        isLoading={pagination.isLoading || pagination.isRefreshing}
        initialWeekStart={weekStart}
        onWeekChange={setWeekStart}
      />
    </Section>
  );
}

export default AvailabilityScheduleSettings;
